// Phase 26A · Shared PageHeader
// Provides: title, subtitle, breadcrumbs, back button, optional star toggle, and right-side actions.
import { useNavigate } from "react-router-dom";
import { ChevronRight, ArrowLeft, Star } from "lucide-react";
import { useFavorites } from "@/lib/favorites";

export default function PageHeader({
  title,
  subtitle,
  back,           // truthy -> show back button. Can be `true` (uses history) or a route string.
  breadcrumbs,    // [{label, to}]  last one is the active page (rendered plain)
  actions,        // JSX rendered on the right
  favorite,       // {key, label, route, type='screen'|'report'} enables star toggle
  testid = "page-header",
}) {
  const navigate = useNavigate();
  const { isFav, toggle } = useFavorites();

  const onBack = () => {
    if (typeof back === "string") navigate(back);
    else if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  const favActive = favorite ? isFav(favorite.key) : false;

  return (
    <div className="mb-6" data-testid={testid}>
      {breadcrumbs?.length ? (
        <nav
          className="flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-neutral-400 mb-2"
          data-testid={`${testid}-breadcrumbs`}
        >
          {breadcrumbs.map((c, i) => {
            const last = i === breadcrumbs.length - 1;
            return (
              <span key={i} className="flex items-center gap-1">
                {c.to && !last ? (
                  <button
                    type="button"
                    onClick={() => navigate(c.to)}
                    className="hover:text-[#6D2C91] transition-colors"
                    data-testid={`${testid}-crumb-${i}`}
                  >
                    {c.label}
                  </button>
                ) : (
                  <span
                    className={last ? "text-neutral-600 font-semibold" : ""}
                    data-testid={`${testid}-crumb-${i}`}
                  >
                    {c.label}
                  </span>
                )}
                {!last && <ChevronRight className="h-3 w-3 text-neutral-300" />}
              </span>
            );
          })}
        </nav>
      ) : null}

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          {back ? (
            <button
              type="button"
              onClick={onBack}
              data-testid={`${testid}-back`}
              className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-600 hover:border-[#6D2C91] hover:text-[#6D2C91] transition-colors mt-1"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          ) : null}

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1
                className="text-2xl sm:text-3xl font-extrabold tracking-tight text-neutral-900 truncate"
                data-testid={`${testid}-title`}
              >
                {title}
              </h1>
              {favorite ? (
                <button
                  type="button"
                  onClick={() =>
                    toggle({
                      item_type: favorite.type || "screen",
                      item_key: favorite.key,
                      item_label: favorite.label || title,
                      item_route: favorite.route,
                    })
                  }
                  data-testid={`${testid}-fav-toggle`}
                  className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-neutral-100 transition-colors"
                  aria-label={favActive ? "Unstar" : "Star"}
                  title={favActive ? "Remove from favorites" : "Add to favorites"}
                >
                  <Star
                    className={`h-4 w-4 transition-colors ${
                      favActive
                        ? "fill-[#E5197F] text-[#E5197F]"
                        : "text-neutral-400"
                    }`}
                  />
                </button>
              ) : null}
            </div>
            {subtitle ? (
              <p
                className="text-sm text-neutral-500 mt-1"
                data-testid={`${testid}-subtitle`}
              >
                {subtitle}
              </p>
            ) : null}
          </div>
        </div>

        {actions ? (
          <div className="flex items-center gap-2" data-testid={`${testid}-actions`}>
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}
